import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChefHat, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { api } from "../lib/api";

/**
 * Phase 22.1 — customer AI recipes widget inside the CartDrawer.
 *
 * On first click, calls ``POST /api/ai/recipes`` (server derives the cart
 * items from JWT / X-Session-Id automatically) and renders the 3 recipes
 * with clickable "missing ingredient" chips that link into search.
 */
export default function CartRecipesSection({ onNavigate }) {
  const [recipes, setRecipes] = useState(null); // null = never fetched, [] = empty
  const [busy, setBusy] = useState(false);

  const fetchRecipes = async () => {
    setBusy(true);
    const t = toast.loading("IA cocinando ideas…");
    try {
      const { data } = await api.post("/ai/recipes", {});
      setRecipes(data.recipes || []);
      if ((data.recipes || []).length === 0) {
        toast.error(data.note || "No pude generar recetas.", { id: t });
      } else {
        toast.success("Recetas listas", { id: t });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        detail === "ai_timeout"
          ? "El servicio de IA está tardando. Inténtalo de nuevo."
          : "IA no disponible en este momento.";
      toast.error(msg, { id: t });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="cart-ai-recipes"
      className="mt-4 rounded-xl border border-neutral-200 bg-white p-3"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <ChefHat className="h-4 w-4 text-[#00953B]" />
          <h3 className="font-black text-sm">Ideas con este carro (IA)</h3>
        </div>
        <Button
          data-testid="cart-ai-recipes-btn"
          size="sm"
          variant={recipes ? "outline" : "default"}
          onClick={fetchRecipes}
          disabled={busy}
          className={`h-8 gap-1 rounded-full ${
            recipes
              ? "border-[#00953B] text-[#00953B] hover:bg-[#F1FAF3]"
              : "bg-[#00953B] hover:bg-[#007a30] text-white"
          }`}
        >
          <Sparkles className={`h-3 w-3 ${busy ? "animate-pulse" : ""}`} />
          {recipes ? "Regenerar" : "Sugerir recetas"}
        </Button>
      </div>
      {recipes === null && (
        <p className="text-xs text-neutral-500 mt-2">
          Descubre 3 recetas que puedes preparar con lo que tienes en el carro.
        </p>
      )}
      {recipes && recipes.length > 0 && (
        <ul className="mt-3 space-y-3" data-testid="cart-ai-recipes-list">
          {recipes.map((r, i) => (
            <li
              key={i}
              data-testid={`cart-ai-recipe-${i}`}
              className="rounded-lg border border-neutral-100 p-2 bg-neutral-50"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-sm">{r.title}</div>
                <div className="text-[10px] text-neutral-500 whitespace-nowrap">
                  {r.difficulty} · {(r.prep_minutes || 0) + (r.cook_minutes || 0)} min · {r.servings || 4} raciones
                </div>
              </div>
              {(r.missing_ingredients || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  <span className="text-[10px] text-neutral-500 pt-1">
                    Faltan:
                  </span>
                  {r.missing_ingredients.map((ing, j) => (
                    <Link
                      key={j}
                      onClick={onNavigate}
                      to={`/buscar?q=${encodeURIComponent(ing)}`}
                      data-testid={`cart-ai-recipe-${i}-missing-${j}`}
                      className="inline-block text-[11px] px-2 py-0.5 rounded-full bg-white border border-neutral-200 hover:border-[#00953B] hover:text-[#00953B]"
                    >
                      {ing}
                    </Link>
                  ))}
                </div>
              )}
              {(r.steps || []).length > 0 && (
                <ol className="mt-2 text-[11px] leading-relaxed text-neutral-700 list-decimal ml-4 space-y-0.5">
                  {r.steps.slice(0, 4).map((s, k) => (
                    <li key={k}>{typeof s === "string" ? s.replace(/^\d+\.\s*/, "") : s}</li>
                  ))}
                </ol>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
